import { createContext, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { accessAPI, readFromLS } from "../../utils";

// Permisos del usuario en la interfaz. Es el espejo de utils/permisos.js de la
// API: por cada permiso, el acceso con el que entró el usuario tiene un nivel.
// La interfaz solo oculta lo que la API igual rechazaría.
//
//   const { puedeVer, puedeEditar } = usePermiso("admin.administradores");
//
//   <ConPermiso clave="admin.administradores" nivel="full">
//     <Boton>Nuevo administrador</Boton>
//   </ConPermiso>
//
// En el menú del Header, un ítem con `permiso: "clave"` se oculta si el usuario
// no tiene al menos lectura.

export const NIVEL = { SIN_ACCESO: 0, LECTURA: 1, FULL: 2 };

const NIVEL_POR_NOMBRE = { lectura: NIVEL.LECTURA, full: NIVEL.FULL };

const SIN_SESION = { accesoTipo: null, permisos: {}, cargando: true };

const ContextoAcceso = createContext(SIN_SESION);

// Carga el rol y los permisos del usuario logueado y los deja disponibles para
// toda la app. Va dentro del router: después del login la app navega sin
// recargar, así que se vuelve a mirar el token en cada cambio de ruta.
export function ProveedorPermisos({ children }) {
  useLocation();
  const token = readFromLS(process.env.REACT_APP_LS_LOGIN_TOKEN);
  const [acceso, setAcceso] = useState(SIN_SESION);

  useEffect(() => {
    setAcceso(SIN_SESION);
    if (!token) return;
    accessAPI(
      "GET",
      "usuario/info-accesos",
      null,
      (respuesta) =>
        setAcceso({
          accesoTipo: respuesta.accesoTipo,
          permisos: respuesta.permisos || {},
          cargando: false
        }),
      () => {}
    );
  }, [token]);

  return (
    <ContextoAcceso.Provider value={acceso}>{children}</ContextoAcceso.Provider>
  );
}

/** { accesoTipo, permisos, cargando } del usuario logueado. */
export function useAcceso() {
  return useContext(ContextoAcceso);
}

/**
 * Nivel del usuario en un permiso. Mientras carga, no puede nada: es preferible
 * que un botón aparezca un instante después a que aparezca y desaparezca.
 */
export function usePermiso(clave) {
  const { permisos, cargando } = useAcceso();
  const nivel = permisos[clave] ?? NIVEL.SIN_ACCESO;
  return {
    nivel,
    cargando,
    puedeVer: nivel >= NIVEL.LECTURA,
    puedeEditar: nivel >= NIVEL.FULL
  };
}

/**
 * Muestra su contenido solo si el usuario tiene el permiso con al menos ese
 * nivel ("lectura" por defecto, o "full"). Mientras carga no muestra nada; si
 * el nivel no alcanza, muestra `alternativa`.
 */
export function ConPermiso({
  clave,
  nivel = "lectura",
  alternativa = null,
  children
}) {
  const permiso = usePermiso(clave);
  if (permiso.cargando) return null;
  return permiso.nivel >= NIVEL_POR_NOMBRE[nivel] ? children : alternativa;
}
